<template>
    <div class="rounded border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
        <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-100">Brokerages</h3>
            <div class="flex gap-2">
                <button type="button"
                        :disabled="!hasCityState"
                        @click="discoverLocals"
                        class="text-xs px-2 py-1 rounded border border-gray-300 dark:border-slate-600
                               text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700
                               disabled:opacity-40 disabled:cursor-not-allowed">
                    <svg v-if="isDiscovering" class="inline-block animate-spin w-3 h-3 mr-1 align-text-top" fill="none" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" class="opacity-25"></circle>
                        <path d="M4 12a8 8 0 018-8" stroke="currentColor" stroke-width="3" class="opacity-75"></path>
                    </svg>
                    Discover locals via Firecrawl
                </button>
                <button type="button" @click="selectAll" class="text-xs underline text-gray-700 dark:text-gray-300">All</button>
                <button type="button" @click="selectNone" class="text-xs underline text-gray-700 dark:text-gray-300">None</button>
            </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <fieldset>
                <div class="flex items-center justify-between mb-1">
                    <legend class="text-xs font-semibold text-gray-600 dark:text-gray-300">National majors</legend>
                    <button v-if="curated.length" type="button" @click="toggleGroup(curated)"
                            class="text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-400 hover:underline">
                        {{ allSelected(curated) ? 'Clear' : 'Select all' }}
                    </button>
                </div>
                <div v-if="curated.length === 0" class="text-xs text-gray-500 dark:text-gray-400">Loading…</div>
                <label v-for="b in curated" :key="b.id"
                       class="flex items-center gap-2 text-sm py-0.5 text-gray-800 dark:text-gray-100">
                    <input type="checkbox" :value="b.id" v-model="selected" />
                    <span>{{ b.name }}</span>
                    <span v-if="b.has_url_template" class="text-[10px] uppercase text-green-700 dark:text-green-300">template</span>
                </label>
            </fieldset>

            <fieldset>
                <div class="flex items-center justify-between mb-1">
                    <legend class="text-xs font-semibold text-gray-600 dark:text-gray-300">
                        Discovered locals
                        <span v-if="hasCityState" class="font-normal text-gray-500 dark:text-gray-400">({{ city }}, {{ stateCode }})</span>
                    </legend>
                    <button v-if="discovered.length" type="button" @click="toggleGroup(discovered)"
                            class="text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-400 hover:underline">
                        {{ allSelected(discovered) ? 'Clear' : 'Select all' }}
                    </button>
                </div>
                <div v-if="!hasCityState" class="text-xs text-gray-500 dark:text-gray-400">
                    Enter a city + state to see locally-discovered firms.
                </div>
                <div v-else-if="discovered.length === 0" class="text-xs text-gray-500 dark:text-gray-400">
                    None yet — click "Discover locals" to search via Firecrawl.
                </div>
                <label v-for="b in discovered" :key="b.id"
                       class="flex items-center gap-2 text-sm py-0.5 text-gray-800 dark:text-gray-100">
                    <input type="checkbox" :value="b.id" v-model="selected" />
                    <span>{{ b.name }}</span>
                    <a v-if="b.website" :href="b.website" target="_blank" rel="noopener noreferrer"
                       class="text-[10px] text-blue-600 dark:text-blue-400 hover:underline">site</a>
                </label>
            </fieldset>

            <fieldset>
                <div class="flex items-center justify-between mb-1">
                    <legend class="text-xs font-semibold text-gray-600 dark:text-gray-300">Aggregators</legend>
                    <button v-if="aggregators.length" type="button" @click="toggleGroup(aggregators)"
                            class="text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-400 hover:underline">
                        {{ allSelected(aggregators) ? 'Clear' : 'Select all' }}
                    </button>
                </div>
                <div v-if="aggregators.length === 0" class="text-xs text-gray-500 dark:text-gray-400">None configured.</div>
                <label v-for="b in aggregators" :key="b.id"
                       class="flex items-center gap-2 text-sm py-0.5 text-gray-800 dark:text-gray-100">
                    <input type="checkbox" :value="b.id" v-model="selected" />
                    <span>{{ b.name }}</span>
                    <span v-if="b.has_url_template" class="text-[10px] uppercase text-green-700 dark:text-green-300">template</span>
                </label>
            </fieldset>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BrokerageSelector',
    data() {
        return {
            curated: [],
            discovered: [],
            aggregators: [],
            isDiscovering: false,
            loadHandle: null,
        };
    },
    computed: {
        criteria() { return this.$store.state.listingSearch.criteria; },
        city() { return this.criteria.city; },
        stateCode() { return this.criteria.state; },
        hasCityState() { return Boolean(this.city && this.stateCode); },
        selected: {
            get() { return this.criteria.brokerage_ids; },
            set(v) { this.$store.commit('SET_LISTING_CRITERIA', { brokerage_ids: v }); },
        },
    },
    watch: {
        city() { this.scheduleReload(); },
        stateCode() { this.scheduleReload(); },
    },
    mounted() {
        this.fetch(false);
    },
    methods: {
        scheduleReload() {
            if (this.loadHandle) clearTimeout(this.loadHandle);
            this.loadHandle = setTimeout(() => this.fetch(false), 400);
        },
        async fetch(discover) {
            const params = {};
            if (this.hasCityState) {
                params.city = this.city;
                params.state = this.stateCode;
            }
            if (discover) params.discover = 1;

            try {
                if (discover) this.isDiscovering = true;
                const { data } = await axios.get('/api/brokerages', { params });
                if (data?.success) {
                    this.curated     = data.data.curated     || [];
                    this.discovered  = data.data.discovered  || [];
                    this.aggregators = data.data.aggregators || [];
                }
            } catch (e) {
                console.warn('Failed to load brokerages', e);
            } finally {
                this.isDiscovering = false;
            }
        },
        discoverLocals() {
            if (!this.hasCityState) return;
            this.fetch(true);
        },
        selectAll() {
            this.selected = [
                ...this.curated.map((b) => b.id),
                ...this.discovered.map((b) => b.id),
                ...this.aggregators.map((b) => b.id),
            ];
        },
        selectNone() {
            this.selected = [];
        },
        allSelected(group) {
            if (!group.length) return false;
            const set = new Set(this.selected);
            return group.every((b) => set.has(b.id));
        },
        toggleGroup(group) {
            const ids = group.map((b) => b.id);
            if (this.allSelected(group)) {
                // Remove this group's ids from selection.
                const remove = new Set(ids);
                this.selected = this.selected.filter((id) => !remove.has(id));
            } else {
                // Union the group into selection (preserving other groups' picks).
                const merged = new Set(this.selected);
                ids.forEach((id) => merged.add(id));
                this.selected = [...merged];
            }
        },
    },
};
</script>
